import React, { useState, useEffect } from 'react';
import { Search, Plus, X, CheckCircle2, Loader2 } from 'lucide-react';
import { supabase } from '../services/supabaseService';
import { DEFAULT_KANBAN_STAGES } from '../constants';

interface VacancyAddCandidateModalProps {
    vacancyId: string;
    clientId?: string;
    onClose: () => void;
    onAdded?: () => void;
}

const VacancyAddCandidateModal: React.FC<VacancyAddCandidateModalProps> = ({ vacancyId, clientId, onClose, onAdded }) => {
    const [poolSearchQuery, setPoolSearchQuery] = useState('');
    const [poolCandidates, setPoolCandidates] = useState<any[]>([]);
    const [selectedPoolIds, setSelectedPoolIds] = useState<string[]>([]);
    const [isPoolLoading, setIsPoolLoading] = useState(false);
    const [isSavingNew, setIsSavingNew] = useState(false);

    // Form Context
    const [targetAddVacancyId, setTargetAddVacancyId] = useState<string>(vacancyId || '');
    const [targetAddStageId, setTargetAddStageId] = useState<string>(
        () => DEFAULT_KANBAN_STAGES.find((s) => s.name === 'Shortlist')?.id || 'Shortlist'
    );
    const [availableVacancies, setAvailableVacancies] = useState<any[]>([]);
    const [vacancySearchQuery, setVacancySearchQuery] = useState('');
    const [targetVacancyDropdownOpen, setTargetVacancyDropdownOpen] = useState(false);
    const [showClosedVacancies, setShowClosedVacancies] = useState(false);

    useEffect(() => {
        fetchPoolAndVacancies();
    }, []);

    const fetchPoolAndVacancies = async () => {
        setIsPoolLoading(true);
        const isGlobal = clientId === '00000000-0000-0000-0000-000000000001' || !clientId;
        try {
            let query = supabase.from('vacancies').select('id, title, status, vacancy_number, data, client_id, clients!vacancies_client_id_fkey(name, logo_url)');
            if (!isGlobal) {
                query = query.eq('client_id', clientId);
            }
            const { data: vacs } = await query;
                
            if (vacs) {
                setAvailableVacancies(vacs.sort((a: any, b: any) => a.title.localeCompare(b.title)));
            }

            if (vacancyId && !vacancyId.startsWith('ALL') && !vacancyId.startsWith('0000')) {
                setTargetAddVacancyId(vacancyId);
                const matched = (vacs || []).find((v:any) => v.id === vacancyId);
                if (matched) setVacancySearchQuery(matched.title);
                const shortlistId = DEFAULT_KANBAN_STAGES.find((s) => s.name === 'Shortlist')?.id || 'Shortlist';
                setTargetAddStageId(shortlistId);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsPoolLoading(false);
        }
    };

    const handleAddFromPool = async () => {
        if (selectedPoolIds.length === 0 || !targetAddVacancyId || !targetAddStageId) return;
        
        let activeClientId = clientId;
        const selectedVac = availableVacancies.find(v => v.id === targetAddVacancyId);
        if (selectedVac && selectedVac.client_id) {
            activeClientId = selectedVac.client_id;
        }
        
        if (!activeClientId || activeClientId === '00000000-0000-0000-0000-000000000001') {
            alert("Kan geen klant-ID bepalen voor de geselecteerde vacature.");
            return;
        }

        setIsSavingNew(true);
        try {
            const toInsert = selectedPoolIds.map(cid => ({
                vacancy_id: targetAddVacancyId,
                candidate_id: cid,
                status: targetAddStageId
            }));

            const { error } = await supabase.from('applications').insert(toInsert);
            if (error) throw error;
            
            setSelectedPoolIds([]);
            if (onAdded) onAdded();
            onClose();
        } catch (e) {
            console.error(e);
            alert('Fout bij toevoegen uit Talentpool');
        } finally {
            setIsSavingNew(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[200] bg-slate-900/60 backdrop-blur-sm transition-opacity animate-in fade-in flex justify-end">
            <div className="bg-white h-full w-full max-w-lg shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
                <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                    <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight">Kandidaat Toevoegen</h2>
                    <button onClick={onClose} className="p-2 hover:bg-slate-50 rounded-full transition-colors"><X size={20} className="text-slate-400" /></button>
                </div>

                <div className="flex-1 px-6 pb-6 pt-2 overflow-y-auto custom-scrollbar flex flex-col min-h-[500px]">
                    <div className="flex flex-col gap-6">
                        {!(vacancyId && !vacancyId.startsWith('ALL') && !vacancyId.startsWith('0000')) && (
                            <>
                                {/* STEP 1: Kies Vacature */}
                                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                                    <div className="flex justify-between items-end mb-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-[#e51b23] block">1. Kies Vacature</label>
                                        <button 
                                            onClick={() => setShowClosedVacancies(!showClosedVacancies)}
                                            className="text-[9px] font-bold text-slate-400 hover:text-slate-600 underline"
                                        >
                                            {showClosedVacancies ? 'Verberg' : 'Toon'} Afgerond/Gesloten
                                        </button>
                                    </div>
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                                        <input 
                                            value={vacancySearchQuery}
                                            onChange={e => { setVacancySearchQuery(e.target.value); setTargetVacancyDropdownOpen(true); }}
                                            onFocus={() => setTargetVacancyDropdownOpen(true)}
                                            onBlur={() => setTimeout(() => setTargetVacancyDropdownOpen(false), 200)}
                                            placeholder="Zoek en selecteer vacature..."
                                            className="w-full pl-9 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-bold outline-none focus:border-indigo-500 shadow-sm transition-all"
                                        />
                                        {targetVacancyDropdownOpen && (
                                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-2xl max-h-56 overflow-y-auto z-50 flex flex-col py-1">
                                                {availableVacancies
                                                    .filter(v => showClosedVacancies || (v.status !== 'Afgerond' && v.status !== 'Gesloten'))
                                                    .filter(v => !vacancySearchQuery || v.title.toLowerCase().includes(vacancySearchQuery.toLowerCase()))
                                                    .map(v => (
                                                        <div 
                                                            key={v.id} 
                                                            className="px-4 py-3 hover:bg-slate-50 cursor-pointer text-sm font-bold text-slate-700 flex justify-between items-center border-b border-slate-50 last:border-0" 
                                                            onClick={() => { setTargetAddVacancyId(v.id); setVacancySearchQuery(v.title); setTargetVacancyDropdownOpen(false); }}
                                                        >
                                                            <div className="flex flex-col gap-1 max-w-[80%]">
                                                                <span className="text-slate-900 truncate" title={v.title}>
                                                                    {v.vacancy_number ? <span className="font-mono text-indigo-500 mr-2">#{v.vacancy_number}</span> : null}
                                                                    {v.title}
                                                                </span>
                                                                <span className="text-[11px] text-slate-500 font-medium truncate">
                                                                    {v.clients?.name || v.data?.werkplek || v.data?.clientName || 'Onbekende Werkplek'} {v.data?.location || v.data?.city ? `- ${v.data?.location || v.data?.city}` : ''}
                                                                </span>
                                                            </div>
                                                            {v.status === 'Afgerond' || v.status === 'Gesloten' ? <span className="text-[10px] text-slate-400 uppercase tracking-widest">({v.status})</span> : null}
                                                        </div>
                                                    ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}

                        {/* STEP 2: Kies Kandidaten uit Talentpool */}
                        <div className={`bg-slate-50 border border-slate-200 rounded-xl p-5 flex flex-col ${(vacancyId && !vacancyId.startsWith('ALL') && !vacancyId.startsWith('0000')) ? 'h-[600px]' : 'h-[400px]'}`}>
                            <label className="text-[10px] font-black uppercase tracking-widest text-[#e51b23] block mb-2">{(vacancyId && !vacancyId.startsWith('ALL') && !vacancyId.startsWith('0000')) ? 'Zoek Kandidaat (Talentpool)' : '2. Kies Kandidaten (Talentpool)'}</label>
                            
                            <div className="border border-slate-200 rounded-xl overflow-hidden flex-1 flex flex-col bg-white">
                                <div className="p-3 border-b border-slate-100 bg-slate-50 relative">
                                    <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                                    <input
                                        value={poolSearchQuery}
                                        onChange={async (e) => {
                                            const val = e.target.value;
                                            setPoolSearchQuery(val);
                                            if (val.trim().length >= 2) {
                                                setIsPoolLoading(true);
                                                try {
                                                    const { supabase_get_paginated_talent_pool } = await import('../services/supabaseService');
                                                    const isGlobal = clientId === '00000000-0000-0000-0000-000000000001' || !clientId;
                                                    const res = await supabase_get_paginated_talent_pool(isGlobal ? undefined : clientId, 1, 50, val);
                                                    if (res && res.data) {
                                                        const extracted = res.data.map((tp: any) => ({
                                                            ...tp.candidate,
                                                            candidate_id: tp.candidate_id || tp.candidate?.id || tp.id,
                                                            ai_parsed_data: tp.ai_parsed_data || tp.candidate?.ai_parsed_data
                                                        })).filter(Boolean);
                                                        const unique = Array.from(new Map(extracted.map((c: any) => [c.candidate_id, c])).values());
                                                        setPoolCandidates(unique);
                                                    } else {
                                                        setPoolCandidates([]);
                                                    }
                                                } catch (err) {
                                                    console.error("Pool search failed", err);
                                                } finally {
                                                    setIsPoolLoading(false);
                                                }
                                            } else {
                                                setPoolCandidates([]);
                                            }
                                        }}
                                        placeholder="Typ minimaal 2 letters om te zoeken in de talentpool..."
                                        className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-bold outline-none focus:border-indigo-500 transition-colors"
                                    />
                                    {isPoolLoading && <Loader2 size={14} className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />}
                                </div>

                                <div className="flex-1 overflow-y-auto custom-scrollbar relative">
                                    {poolCandidates.length === 0 && poolSearchQuery.length < 2 ? (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                            <Search size={28} className="text-slate-200 mb-3" />
                                            <span className="text-[11px] font-medium text-slate-400">Typ minimaal 2 letters om te zoeken in de talentpool...</span>
                                        </div>
                                    ) : poolCandidates.length === 0 && poolSearchQuery.length >= 2 && !isPoolLoading ? (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-[11px] text-slate-400">
                                            Geen kandidaten gevonden in de talentpool.
                                        </div>
                                    ) : (
                                        poolCandidates.map(candidate => {
                                            const isSelected = selectedPoolIds.includes(candidate.candidate_id);
                                            return (
                                                <div
                                                    key={candidate.id}
                                                    onClick={() => {
                                                        if (isSelected) setSelectedPoolIds(prev => prev.filter(id => id !== candidate.candidate_id));
                                                        else setSelectedPoolIds(prev => [...prev, candidate.candidate_id]);
                                                    }}
                                                    className={`p-3 border-b border-slate-100 last:border-0 flex items-center gap-3 cursor-pointer transition-colors ${isSelected ? 'bg-[#e51b23]/5' : 'hover:bg-slate-50'}`}
                                                >
                                                    <div className={`shrink-0 w-4 h-4 rounded border flex items-center justify-center transition-colors ${isSelected ? 'bg-[#e51b23] border-[#e51b23] text-white' : 'border-slate-300 bg-white'}`}>
                                                        {isSelected && <CheckCircle2 size={10} />}
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2">
                                                            <p className="text-sm font-bold text-slate-700">{candidate.name || 'Naamloos'}</p>
                                                        </div>
                                                        <p className="text-[10px] text-slate-500 mt-0.5">
                                                            {candidate.candidate_number ? <span className="font-mono text-indigo-500 font-bold mr-1">#{candidate.candidate_number}</span> : null}
                                                            {[candidate.role || candidate.ai_parsed_data?.currentRole || candidate.ai_parsed_data?.role || candidate.parsed_cv?.currentRole || candidate.parsed_cv?.role, candidate.city || candidate.address_city].filter(Boolean).join(' • ')}
                                                        </p>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        </div>

                        {!(vacancyId && !vacancyId.startsWith('ALL') && !vacancyId.startsWith('0000')) && (
                            <>
                                {/* STEP 3: Kies Pipeline Status */}
                                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-[#e51b23] mb-2 block">3. Kies Pipeline Status</label>
                                    <select 
                                        value={targetAddStageId}
                                        onChange={(e) => setTargetAddStageId(e.target.value)}
                                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 outline-none focus:border-indigo-500 shadow-sm cursor-pointer"
                                    >
                                        {DEFAULT_KANBAN_STAGES.map(s => (
                                            <option key={s.id} value={s.id}>{s.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </>
                        )}

                        {/* STEP 4: Submit Button */}
                        <button
                            onClick={handleAddFromPool}
                            disabled={selectedPoolIds.length === 0 || !targetAddVacancyId || !targetAddStageId || isSavingNew}
                            className="w-full py-4 mt-2 bg-slate-900 text-white rounded-xl font-black text-sm uppercase tracking-widest hover:bg-black transition-all flex items-center justify-center gap-2 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSavingNew ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />} {selectedPoolIds.length} Kandidaten Toevoegen { (vacancyId && !vacancyId.startsWith('ALL') && !vacancyId.startsWith('0000')) ? 'aan Shortlist' : '' }
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VacancyAddCandidateModal;
